const authoring = {
  en: {
    games: {
      unknownError: 'Unknown error',
      accessDenied: "You don't have access to view these games.",
      title: 'Games',
      lessonLabel: 'Lesson: {title}',
      close: 'Close',
      loading: 'Loading games...',
      count: '{count} games',
      countOne: '1 game',
      newGame: '+ New game',
      emptyState: 'No games for this lesson yet. Choose a game type above to get started.',
      deleteConfirm: 'Delete this game? This cannot be undone.',
      deleteTitle: 'Delete game',
      deleting: 'Deleting...',
      delete: 'Delete',
      badge: {
        story: 'Story',
        puzzle: 'Puzzle',
        simulator: 'Simulator',
        quest: 'Quest',
      },
      types: {
        storyAdventure: 'Story Adventure',
        puzzleMaster: 'Puzzle Master',
        simulator: 'Simulator',
        skillQuest: 'Skill Quest',
      },
      form: {
        gameType: 'Game type',
        title: 'Title',
        titlePlaceholder: 'Game title',
        description: 'Description',
        descriptionPlaceholder: 'Optional description',
        cancel: 'Cancel',
        creating: 'Creating...',
        createGame: 'Create game',
      },
      story: {
        nodesTitle: 'Story nodes ({count})',
        addNode: '+ Add node',
        emptyNodes: 'No nodes yet. Add a start node to begin authoring the story.',
        nodeTypePrompt: 'Prompt',
        nodeTypeTerminal: 'Terminal',
        nodeTextPlaceholder: 'Node text',
        scorePlaceholder: 'Score',
        scoreOnReachTitle: 'Score on reach',
        startLabel: 'Start',
        startTitle: 'Mark as start node',
        cancel: 'Cancel',
        adding: 'Adding...',
        addNodeBtn: 'Add node',
        startBadge: 'START',
        ptsSuffix: '+{score} pts',
        addChoice: '+ Add choice',
        choiceLabelPlaceholder: 'Choice label (button text)',
        leadsTo: '-- leads to --',
        savingChoice: 'Saving...',
        addChoiceBtn: 'Add choice',
      },
      puzzle: {
        title: 'Puzzles ({count})',
        addPuzzle: '+ Add puzzle',
        emptyPuzzles: 'No puzzles yet. Add the first puzzle to start authoring.',
        questionLabel: 'Question / prompt',
        questionPlaceholder: 'What question will the student see?',
        answerLabel: 'Correct answer',
        answerPlaceholder: 'Expected answer (case-insensitive)',
        hintsLabel: 'Hints (one per line)',
        hintsPlaceholder: 'Optional hints, one per line',
        pointsLabel: 'Points for correct answer',
        cancel: 'Cancel',
        adding: 'Adding...',
        addPuzzleBtn: 'Add puzzle',
        answerPrefix: 'Answer:',
        hintsPrefix: 'Hints:',
      },
      simulator: {
        stepPromptLabel: 'Step prompt',
        stepPromptPlaceholder: 'What situation or question does the student face?',
        expectedActionLabel: 'Expected action',
        expectedActionPlaceholder: 'What should the student do or say?',
        feedbackLabel: 'Feedback',
        feedbackPlaceholder: 'Explanation shown after the student responds',
        pointsLabel: 'Points for correct action',
        cancel: 'Cancel',
        adding: 'Adding...',
        addStep: 'Add step',
        stepsCount: '{count} steps',
        stepsCountOne: '1 step',
        emptySteps: 'No steps yet. Add at least 3 steps to make this scenario playable.',
        addStepBtn: '+ Add step',
        stepLabel: 'Step {num}',
        expectedPrefix: 'Expected:',
        feedbackPrefix: 'Feedback:',
        scenarioTitle: 'Scenario title',
        scenarioTitlePlaceholder: 'e.g. Customer objection handling',
        descriptionLabel: 'Description (optional)',
        descriptionPlaceholder: 'Brief description of this scenario',
        creating: 'Creating...',
        createScenario: 'Create scenario',
        scenariosTitle: 'Simulator scenarios ({count})',
        addScenario: '+ Add scenario',
        loadingScenarios: 'Loading scenarios...',
        emptyScenarios: 'No scenarios yet. Add a scenario and at least 3 steps to make this game playable.',
      },
      quest: {
        nodeTitleLabel: 'Node title',
        nodeTitlePlaceholder: 'e.g. Introduction Challenge',
        nodeTypeLabel: 'Node type',
        descriptionLabel: 'Description (optional)',
        descriptionPlaceholder: 'What skill or challenge does this node cover?',
        scoreRequiredLabel: 'Score required to unlock next node',
        scoreRequiredHint: ' (0 = no score gate)',
        cancel: 'Cancel',
        adding: 'Adding...',
        addNodeBtn: 'Add node',
        loadingSkillTree: 'Loading skill tree...',
        emptySkills: 'No skills linked to quest nodes yet. Skills are associated via the backend skill tree API.',
        nodesTitle: 'Quest nodes ({count})',
        hideSkillTree: 'Hide skill tree',
        skillTree: 'Skill tree',
        toggleSkillTreeTitle: 'Toggle skill tree view',
        addNode: '+ Add node',
        loadingNodes: 'Loading nodes...',
        linkedSkills: 'Linked skills',
        emptyNodes: 'No nodes yet. Add at least 3 nodes (CHALLENGE, CHECKPOINT, or BOSS) to build the quest path.',
        needsPtsToUnlock: 'Needs {score} pts to unlock next',
        types: {
          challenge: 'Challenge',
          checkpoint: 'Checkpoint',
          boss: 'Boss',
        },
      },
    },
    video: {
      titleInputPlaceholder: 'Write title',
      titleHint: '*Write title',
      previousVideo: 'Previous video',
      nextVideo: 'Next video',
      playVideo: 'Play video',
      toggleSelect: 'Select this video',
      selectVideoNamed: 'Select video: {title}',
      emptyTitle: 'No videos yet',
      emptyDescription: 'Videos you add to this lesson will appear here.',
    },
    cover: {
      icons: {
        none: 'None',
        development: 'Development',
        business: 'Business',
        language: 'Language',
        design: 'Design',
        marketing: 'Marketing',
        data: 'Data',
        technology: 'Technology',
        creative: 'Creative',
        academic: 'Academic',
      },
      palette: {
        indigo: 'Indigo',
        amber: 'Amber',
        emerald: 'Emerald',
        rose: 'Rose',
        sky: 'Sky',
        violet: 'Violet',
        midnight: 'Midnight',
        sunset: 'Sunset',
      },
      paletteBackgroundAriaLabel: '{name} background',
      tabDesign: 'Design cover',
      tabUpload: 'Upload image',
      noCover: 'No cover yet',
      previewAlt: 'Course cover preview',
      titleLabel: 'Cover title',
      titlePlaceholder: 'e.g. Python Basics',
      background: 'Background',
      icon: 'Icon',
      chooseImage: 'Choose image',
      dropHint: 'or drop a picture here',
      uploading: 'Uploading…',
      remove: 'Remove',
      hintDesign: 'Your cover is built here — pick a style, an icon and write your title.',
      hintUpload: 'PNG, JPG or WEBP · up to {mb} MB · 16:9 recommended',
      errorFileType: 'Please choose an image file (PNG, JPG, or WEBP).',
      errorFileSize: 'Image must be under {mb} MB.',
      errorNoUrl: 'Upload succeeded but no image URL was returned.',
      errorUploadFailed: 'Upload failed. Please try again.',
      errorNoPermission: "You don't have permission to upload a cover image.",
    },
    learning: {
      selectContentPlaceholder: 'Select content',
      module1: 'Module 1',
      module2: 'Module 2',
      jobs: 'Jobs',
      subjects: 'Subjects',
      hintSelectContent: '*Select content for learning',
      saveContinue: 'Save & continue',
      startTimeDatePlaceholder: 'Start time and date',
      hintSelectTime: '*Select time for learning',
      startDegree: 'Start degree',
      endDegree: 'End degree',
      degrees: {
        a1: 'A1 Beginner',
        a2: 'A2 Elementary',
        b1: 'B1 Intermediate',
        b2: 'B2 Upper Intermediate',
        c1: 'C1 Advanced / Export',
        c2: 'C2 Proficient / Master',
      },
      hintDegree: '*Start and end degree',
      selectCategory: 'Select category',
      categories: {
        chemistry: 'Chemistry',
        uzbekHistory: 'Uzbek history',
        russianLanguage: 'Russian language',
        englishLanguage: 'English language',
        mathematics: 'Mathematics',
        history: 'History',
        biology: 'Biology',
      },
      hintCategoryMonth: '*Select category and month for learning 2',
      selectMonth: 'Select month',
      monthFree: 'Free',
      edit: 'Edit',
      delete: 'Delete',
    },
  },
  ru: {
    games: {
      unknownError: 'Неизвестная ошибка',
      accessDenied: 'У вас нет доступа для просмотра этих игр.',
      title: 'Игры',
      lessonLabel: 'Урок: {title}',
      close: 'Закрыть',
      loading: 'Загрузка игр...',
      count: '{count} игр',
      countOne: '1 игра',
      newGame: '+ Новая игра',
      emptyState: 'Для этого урока пока нет игр. Выберите тип игры выше, чтобы начать.',
      deleteConfirm: 'Удалить эту игру? Это действие нельзя отменить.',
      deleteTitle: 'Удалить игру',
      deleting: 'Удаление...',
      delete: 'Удалить',
      badge: {
        story: 'История',
        puzzle: 'Головоломка',
        simulator: 'Симулятор',
        quest: 'Квест',
      },
      types: {
        storyAdventure: 'Сюжетное приключение',
        puzzleMaster: 'Мастер головоломок',
        simulator: 'Симулятор',
        skillQuest: 'Квест навыков',
      },
      form: {
        gameType: 'Тип игры',
        title: 'Название',
        titlePlaceholder: 'Название игры',
        description: 'Описание',
        descriptionPlaceholder: 'Необязательное описание',
        cancel: 'Отмена',
        creating: 'Создание...',
        createGame: 'Создать игру',
      },
      story: {
        nodesTitle: 'Узлы истории ({count})',
        addNode: '+ Добавить узел',
        emptyNodes: 'Узлов пока нет. Добавьте начальный узел, чтобы начать создание истории.',
        nodeTypePrompt: 'Вопрос',
        nodeTypeTerminal: 'Финал',
        nodeTextPlaceholder: 'Текст узла',
        scorePlaceholder: 'Баллы',
        scoreOnReachTitle: 'Баллы при достижении',
        startLabel: 'Старт',
        startTitle: 'Отметить как начальный узел',
        cancel: 'Отмена',
        adding: 'Добавление...',
        addNodeBtn: 'Добавить узел',
        startBadge: 'СТАРТ',
        ptsSuffix: '+{score} баллов',
        addChoice: '+ Добавить вариант',
        choiceLabelPlaceholder: 'Текст варианта (текст кнопки)',
        leadsTo: '-- ведёт к --',
        savingChoice: 'Сохранение...',
        addChoiceBtn: 'Добавить вариант',
      },
      puzzle: {
        title: 'Головоломки ({count})',
        addPuzzle: '+ Добавить головоломку',
        emptyPuzzles: 'Головоломок пока нет. Добавьте первую, чтобы начать.',
        questionLabel: 'Вопрос / задание',
        questionPlaceholder: 'Какой вопрос увидит студент?',
        answerLabel: 'Правильный ответ',
        answerPlaceholder: 'Ожидаемый ответ (без учёта регистра)',
        hintsLabel: 'Подсказки (по одной на строку)',
        hintsPlaceholder: 'Необязательные подсказки, по одной на строку',
        pointsLabel: 'Баллы за правильный ответ',
        cancel: 'Отмена',
        adding: 'Добавление...',
        addPuzzleBtn: 'Добавить головоломку',
        answerPrefix: 'Ответ:',
        hintsPrefix: 'Подсказки:',
      },
      simulator: {
        stepPromptLabel: 'Описание шага',
        stepPromptPlaceholder: 'С какой ситуацией или вопросом столкнётся студент?',
        expectedActionLabel: 'Ожидаемое действие',
        expectedActionPlaceholder: 'Что должен сделать или сказать студент?',
        feedbackLabel: 'Обратная связь',
        feedbackPlaceholder: 'Пояснение, показываемое после ответа студента',
        pointsLabel: 'Баллы за правильное действие',
        cancel: 'Отмена',
        adding: 'Добавление...',
        addStep: 'Добавить шаг',
        stepsCount: '{count} шагов',
        stepsCountOne: '1 шаг',
        emptySteps: 'Шагов пока нет. Добавьте минимум 3 шага, чтобы сценарий был играбельным.',
        addStepBtn: '+ Добавить шаг',
        stepLabel: 'Шаг {num}',
        expectedPrefix: 'Ожидается:',
        feedbackPrefix: 'Обратная связь:',
        scenarioTitle: 'Название сценария',
        scenarioTitlePlaceholder: 'например, Работа с возражениями клиента',
        descriptionLabel: 'Описание (необязательно)',
        descriptionPlaceholder: 'Краткое описание этого сценария',
        creating: 'Создание...',
        createScenario: 'Создать сценарий',
        scenariosTitle: 'Сценарии симулятора ({count})',
        addScenario: '+ Добавить сценарий',
        loadingScenarios: 'Загрузка сценариев...',
        emptyScenarios: 'Сценариев пока нет. Добавьте сценарий и минимум 3 шага, чтобы игра стала играбельной.',
      },
      quest: {
        nodeTitleLabel: 'Название узла',
        nodeTitlePlaceholder: 'например, Вводное испытание',
        nodeTypeLabel: 'Тип узла',
        descriptionLabel: 'Описание (необязательно)',
        descriptionPlaceholder: 'Какой навык или испытание охватывает этот узел?',
        scoreRequiredLabel: 'Баллы, необходимые для открытия следующего узла',
        scoreRequiredHint: ' (0 = без порога баллов)',
        cancel: 'Отмена',
        adding: 'Добавление...',
        addNodeBtn: 'Добавить узел',
        loadingSkillTree: 'Загрузка дерева навыков...',
        emptySkills: 'К узлам квеста пока не привязаны навыки. Навыки связываются через API дерева навыков на бэкенде.',
        nodesTitle: 'Узлы квеста ({count})',
        hideSkillTree: 'Скрыть дерево навыков',
        skillTree: 'Дерево навыков',
        toggleSkillTreeTitle: 'Показать/скрыть дерево навыков',
        addNode: '+ Добавить узел',
        loadingNodes: 'Загрузка узлов...',
        linkedSkills: 'Связанные навыки',
        emptyNodes: 'Узлов пока нет. Добавьте минимум 3 узла (CHALLENGE, CHECKPOINT или BOSS), чтобы построить путь квеста.',
        needsPtsToUnlock: 'Нужно {score} баллов, чтобы открыть следующий',
        types: {
          challenge: 'Испытание',
          checkpoint: 'Контрольная точка',
          boss: 'Босс',
        },
      },
    },
    video: {
      titleInputPlaceholder: 'Введите название',
      titleHint: '*Введите название',
      previousVideo: 'Предыдущее видео',
      nextVideo: 'Следующее видео',
      playVideo: 'Воспроизвести видео',
      toggleSelect: 'Выбрать это видео',
      selectVideoNamed: 'Выбрать видео: {title}',
      emptyTitle: 'Пока нет видео',
      emptyDescription: 'Видео, добавленные к этому уроку, появятся здесь.',
    },
    cover: {
      icons: {
        none: 'Нет',
        development: 'Разработка',
        business: 'Бизнес',
        language: 'Язык',
        design: 'Дизайн',
        marketing: 'Маркетинг',
        data: 'Данные',
        technology: 'Технологии',
        creative: 'Творчество',
        academic: 'Академическое',
      },
      palette: {
        indigo: 'Индиго',
        amber: 'Янтарный',
        emerald: 'Изумрудный',
        rose: 'Розовый',
        sky: 'Небесный',
        violet: 'Фиолетовый',
        midnight: 'Полуночный',
        sunset: 'Закат',
      },
      paletteBackgroundAriaLabel: 'Фон {name}',
      tabDesign: 'Создать обложку',
      tabUpload: 'Загрузить изображение',
      noCover: 'Обложки пока нет',
      previewAlt: 'Предпросмотр обложки курса',
      titleLabel: 'Заголовок обложки',
      titlePlaceholder: 'например, Основы Python',
      background: 'Фон',
      icon: 'Иконка',
      chooseImage: 'Выбрать изображение',
      dropHint: 'или перетащите картинку сюда',
      uploading: 'Загрузка…',
      remove: 'Удалить',
      hintDesign: 'Обложка создаётся здесь — выберите стиль, иконку и введите заголовок.',
      hintUpload: 'PNG, JPG или WEBP · до {mb} МБ · рекомендуется 16:9',
      errorFileType: 'Выберите файл изображения (PNG, JPG или WEBP).',
      errorFileSize: 'Размер изображения должен быть меньше {mb} МБ.',
      errorNoUrl: 'Загрузка прошла успешно, но ссылка на изображение не была получена.',
      errorUploadFailed: 'Не удалось загрузить файл. Попробуйте ещё раз.',
      errorNoPermission: 'У вас нет прав на загрузку обложки.',
    },
    learning: {
      selectContentPlaceholder: 'Выберите контент',
      module1: 'Модуль 1',
      module2: 'Модуль 2',
      jobs: 'Работа',
      subjects: 'Предметы',
      hintSelectContent: '*Выберите контент для обучения',
      saveContinue: 'Сохранить и продолжить',
      startTimeDatePlaceholder: 'Дата и время начала',
      hintSelectTime: '*Выберите время для обучения',
      startDegree: 'Начальный уровень',
      endDegree: 'Конечный уровень',
      degrees: {
        a1: 'A1 Начальный',
        a2: 'A2 Элементарный',
        b1: 'B1 Средний',
        b2: 'B2 Выше среднего',
        c1: 'C1 Продвинутый / Эксперт',
        c2: 'C2 Свободный / Мастер',
      },
      hintDegree: '*Начальный и конечный уровень',
      selectCategory: 'Выберите категорию',
      categories: {
        chemistry: 'Химия',
        uzbekHistory: 'История Узбекистана',
        russianLanguage: 'Русский язык',
        englishLanguage: 'Английский язык',
        mathematics: 'Математика',
        history: 'История',
        biology: 'Биология',
      },
      hintCategoryMonth: '*Выберите категорию и месяц для обучения 2',
      selectMonth: 'Выберите месяц',
      monthFree: 'Бесплатно',
      edit: 'Изменить',
      delete: 'Удалить',
    },
  },
  uz: {
    games: {
      unknownError: "Noma'lum xatolik",
      accessDenied: "Sizda bu o'yinlarni ko'rish uchun ruxsat yo'q.",
      title: "O'yinlar",
      lessonLabel: 'Dars: {title}',
      close: 'Yopish',
      loading: "O'yinlar yuklanmoqda...",
      count: '{count} ta o\'yin',
      countOne: '1 ta o\'yin',
      newGame: "+ Yangi o'yin",
      emptyState: "Bu dars uchun hali o'yinlar yo'q. Boshlash uchun yuqoridan o'yin turini tanlang.",
      deleteConfirm: "Bu o'yinni o'chirasizmi? Bu amalni bekor qilib bo'lmaydi.",
      deleteTitle: "O'yinni o'chirish",
      deleting: "O'chirilmoqda...",
      delete: "O'chirish",
      badge: {
        story: 'Hikoya',
        puzzle: 'Boshqotirma',
        simulator: 'Simulyator',
        quest: 'Kvest',
      },
      types: {
        storyAdventure: 'Hikoyaviy sarguzasht',
        puzzleMaster: 'Boshqotirma ustasi',
        simulator: 'Simulyator',
        skillQuest: "Ko'nikma kvesti",
      },
      form: {
        gameType: "O'yin turi",
        title: 'Nomi',
        titlePlaceholder: "O'yin nomi",
        description: 'Tavsif',
        descriptionPlaceholder: 'Ixtiyoriy tavsif',
        cancel: 'Bekor qilish',
        creating: 'Yaratilmoqda...',
        createGame: "O'yin yaratish",
      },
      story: {
        nodesTitle: 'Hikoya tugunlari ({count})',
        addNode: "+ Tugun qo'shish",
        emptyNodes: "Hali tugunlar yo'q. Hikoya yozishni boshlash uchun boshlang'ich tugun qo'shing.",
        nodeTypePrompt: 'Savol',
        nodeTypeTerminal: 'Yakuniy',
        nodeTextPlaceholder: 'Tugun matni',
        scorePlaceholder: 'Ball',
        scoreOnReachTitle: 'Yetganda beriladigan ball',
        startLabel: 'Boshlanish',
        startTitle: "Boshlang'ich tugun sifatida belgilash",
        cancel: 'Bekor qilish',
        adding: "Qo'shilmoqda...",
        addNodeBtn: "Tugun qo'shish",
        startBadge: 'BOSHLANISH',
        ptsSuffix: '+{score} ball',
        addChoice: "+ Variant qo'shish",
        choiceLabelPlaceholder: 'Variant matni (tugma matni)',
        leadsTo: '-- olib boradi --',
        savingChoice: 'Saqlanmoqda...',
        addChoiceBtn: "Variant qo'shish",
      },
      puzzle: {
        title: 'Boshqotirmalar ({count})',
        addPuzzle: "+ Boshqotirma qo'shish",
        emptyPuzzles: "Hali boshqotirmalar yo'q. Boshlash uchun birinchi boshqotirmani qo'shing.",
        questionLabel: 'Savol / topshiriq',
        questionPlaceholder: "O'quvchi qanday savolni ko'radi?",
        answerLabel: "To'g'ri javob",
        answerPlaceholder: 'Kutilayotgan javob (katta-kichik harflarga sezgir emas)',
        hintsLabel: 'Maslahatlar (har birini alohida qatorda)',
        hintsPlaceholder: 'Ixtiyoriy maslahatlar, har birini alohida qatorda',
        pointsLabel: "To'g'ri javob uchun ball",
        cancel: 'Bekor qilish',
        adding: "Qo'shilmoqda...",
        addPuzzleBtn: "Boshqotirma qo'shish",
        answerPrefix: 'Javob:',
        hintsPrefix: 'Maslahatlar:',
      },
      simulator: {
        stepPromptLabel: 'Qadam matni',
        stepPromptPlaceholder: "O'quvchi qanday vaziyat yoki savol bilan duch keladi?",
        expectedActionLabel: 'Kutilayotgan harakat',
        expectedActionPlaceholder: "O'quvchi nima qilishi yoki aytishi kerak?",
        feedbackLabel: 'Fikr-mulohaza',
        feedbackPlaceholder: "O'quvchi javob berganidan keyin ko'rsatiladigan tushuntirish",
        pointsLabel: "To'g'ri harakat uchun ball",
        cancel: 'Bekor qilish',
        adding: "Qo'shilmoqda...",
        addStep: "Qadam qo'shish",
        stepsCount: '{count} ta qadam',
        stepsCountOne: '1 ta qadam',
        emptySteps: "Hali qadamlar yo'q. Ssenariyni o'ynash mumkin qilish uchun kamida 3 ta qadam qo'shing.",
        addStepBtn: "+ Qadam qo'shish",
        stepLabel: '{num}-qadam',
        expectedPrefix: 'Kutilmoqda:',
        feedbackPrefix: 'Fikr-mulohaza:',
        scenarioTitle: 'Ssenariy nomi',
        scenarioTitlePlaceholder: 'masalan, Mijoz e\'tirozlari bilan ishlash',
        descriptionLabel: 'Tavsif (ixtiyoriy)',
        descriptionPlaceholder: 'Ushbu ssenariyning qisqacha tavsifi',
        creating: 'Yaratilmoqda...',
        createScenario: 'Ssenariy yaratish',
        scenariosTitle: 'Simulyator ssenariylari ({count})',
        addScenario: '+ Ssenariy qo\'shish',
        loadingScenarios: 'Ssenariylar yuklanmoqda...',
        emptyScenarios: "Hali ssenariylar yo'q. O'yinni o'ynash mumkin qilish uchun ssenariy va kamida 3 ta qadam qo'shing.",
      },
      quest: {
        nodeTitleLabel: 'Tugun nomi',
        nodeTitlePlaceholder: 'masalan, Kirish sinovi',
        nodeTypeLabel: 'Tugun turi',
        descriptionLabel: 'Tavsif (ixtiyoriy)',
        descriptionPlaceholder: 'Bu tugun qaysi ko\'nikma yoki sinovni qamrab oladi?',
        scoreRequiredLabel: 'Keyingi tugunni ochish uchun kerakli ball',
        scoreRequiredHint: " (0 = ball chegarasi yo'q)",
        cancel: 'Bekor qilish',
        adding: "Qo'shilmoqda...",
        addNodeBtn: "Tugun qo'shish",
        loadingSkillTree: "Ko'nikmalar daraxti yuklanmoqda...",
        emptySkills: "Kvest tugunlariga hali ko'nikmalar bog'lanmagan. Ko'nikmalar backenddagi ko'nikmalar daraxti API orqali bog'lanadi.",
        nodesTitle: 'Kvest tugunlari ({count})',
        hideSkillTree: "Ko'nikmalar daraxtini yashirish",
        skillTree: "Ko'nikmalar daraxti",
        toggleSkillTreeTitle: "Ko'nikmalar daraxtini ko'rsatish/yashirish",
        addNode: "+ Tugun qo'shish",
        loadingNodes: 'Tugunlar yuklanmoqda...',
        linkedSkills: "Bog'langan ko'nikmalar",
        emptyNodes: "Hali tugunlar yo'q. Kvest yo'lini qurish uchun kamida 3 ta tugun (CHALLENGE, CHECKPOINT yoki BOSS) qo'shing.",
        needsPtsToUnlock: 'Keyingisini ochish uchun {score} ball kerak',
        types: {
          challenge: 'Sinov',
          checkpoint: 'Nazorat nuqtasi',
          boss: 'Boss',
        },
      },
    },
    video: {
      titleInputPlaceholder: 'Nomini yozing',
      titleHint: '*Nomini yozing',
      previousVideo: 'Oldingi video',
      nextVideo: 'Keyingi video',
      playVideo: 'Videoni ijro etish',
      toggleSelect: 'Ushbu videoni tanlash',
      selectVideoNamed: 'Videoni tanlash: {title}',
      emptyTitle: "Hozircha video yo'q",
      emptyDescription: 'Ushbu darsga qo\'shgan videolaringiz shu yerda ko\'rinadi.',
    },
    cover: {
      icons: {
        none: "Yo'q",
        development: 'Dasturlash',
        business: 'Biznes',
        language: 'Til',
        design: 'Dizayn',
        marketing: 'Marketing',
        data: "Ma'lumotlar",
        technology: 'Texnologiya',
        creative: 'Ijodkorlik',
        academic: 'Akademik',
      },
      palette: {
        indigo: 'Indigo',
        amber: 'Amber',
        emerald: 'Zumrad',
        rose: 'Pushti',
        sky: 'Osmon',
        violet: 'Binafsha',
        midnight: 'Tungi',
        sunset: 'Quyosh botishi',
      },
      paletteBackgroundAriaLabel: '{name} foni',
      tabDesign: 'Muqovani yaratish',
      tabUpload: 'Rasm yuklash',
      noCover: "Hali muqova yo'q",
      previewAlt: 'Kurs muqovasi ko\'rinishi',
      titleLabel: 'Muqova sarlavhasi',
      titlePlaceholder: 'masalan, Python asoslari',
      background: 'Fon',
      icon: 'Ikonka',
      chooseImage: 'Rasm tanlash',
      dropHint: 'yoki rasmni shu yerga tashlang',
      uploading: 'Yuklanmoqda…',
      remove: "O'chirish",
      hintDesign: "Muqova shu yerda yaratiladi — uslub, ikonka tanlang va sarlavha yozing.",
      hintUpload: "PNG, JPG yoki WEBP · {mb} MB gacha · 16:9 tavsiya etiladi",
      errorFileType: 'Rasm faylini tanlang (PNG, JPG yoki WEBP).',
      errorFileSize: "Rasm hajmi {mb} MB dan kichik bo'lishi kerak.",
      errorNoUrl: "Yuklash muvaffaqiyatli bo'ldi, lekin rasm havolasi qaytmadi.",
      errorUploadFailed: "Yuklab bo'lmadi. Qaytadan urinib ko'ring.",
      errorNoPermission: "Muqova yuklash uchun ruxsatingiz yo'q.",
    },
    learning: {
      selectContentPlaceholder: 'Kontentni tanlang',
      module1: '1-modul',
      module2: '2-modul',
      jobs: 'Kasblar',
      subjects: 'Fanlar',
      hintSelectContent: "*O'qish uchun kontentni tanlang",
      saveContinue: 'Saqlash va davom etish',
      startTimeDatePlaceholder: 'Boshlanish sanasi va vaqti',
      hintSelectTime: "*O'qish uchun vaqtni tanlang",
      startDegree: "Boshlang'ich daraja",
      endDegree: 'Yakuniy daraja',
      degrees: {
        a1: "A1 Boshlang'ich",
        a2: 'A2 Elementar',
        b1: "B1 O'rta",
        b2: "B2 O'rtadan yuqori",
        c1: 'C1 Yuqori / Ekspert',
        c2: 'C2 Erkin / Master',
      },
      hintDegree: "*Boshlang'ich va yakuniy daraja",
      selectCategory: 'Kategoriyani tanlang',
      categories: {
        chemistry: 'Kimyo',
        uzbekHistory: "O'zbekiston tarixi",
        russianLanguage: 'Rus tili',
        englishLanguage: 'Ingliz tili',
        mathematics: 'Matematika',
        history: 'Tarix',
        biology: 'Biologiya',
      },
      hintCategoryMonth: "*O'qish uchun kategoriya va oyni tanlang 2",
      selectMonth: 'Oyni tanlang',
      monthFree: "Bepul",
      edit: 'Tahrirlash',
      delete: "O'chirish",
    },
  },
};

export default authoring;
